<template>
  <ion-page>
    <ion-content class="ion-no-padding">
      <div class="app-content-wrapper">
        <template v-if="user && settingsStore.initialized">

          <main-suite v-if="isRootPath" :user="userData" />

          <div v-show="!isRootPath && serviceStore.isSelected && settingsStore.services[serviceStore.selected?.id]" class="iframe-container">
            <div
                v-for="service in settingsStore.services"
                :key="service.id"
                v-show="serviceStore.selected?.id === service.id && !service.isRoot"
                class="iframe-container"
                :data-service-id="service.id"
            >
              <template v-if="cachedUrls[service.id]">
                <iframe
                    :src="cachedUrls[service.id]"
                    class="service-iframe"
                    allow="autoplay; camera; microphone; fullscreen; display-capture"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                ></iframe>
              </template>
              <div v-else class="ion-padding ion-text-center">
                <ion-spinner name="crescent"></ion-spinner>
                <p>Loading service {{ service.id }}...</p>
              </div>
            </div>
          </div>

          <div v-if="!isRootPath && serviceStore.isSelected && !settingsStore.services[serviceStore.selected?.id]" class="ion-padding ion-text-center">
            <p>Service not found</p>
          </div>
        </template>

        <div v-else-if="loading" class="ion-padding ion-text-center center-spinner">
          <ion-spinner name="crescent"></ion-spinner>
          <p>Initializing Suite...</p>
        </div>

        <div v-else></div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { IonPage, IonContent, IonSpinner} from '@ionic/vue';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useAuth } from '@/composables/useAuth';
import { useNavigation } from '@/composables/useNavigation';
import { useSettingsStore } from '@/stores/useSettingsStore';
import MainSuite from '@/components/MainSuite.vue';

import { useServiceStore } from "@/stores/useServiceStore.ts";
import {toast} from "@/utils/notifications.ts";

const { isLoginPage, isRootPath, buildServiceUrl } = useNavigation();
const settingsStore = useSettingsStore();
const serviceStore = useServiceStore();
const router = useRouter();
const { t } = useI18n();
const { user, userData, loading } = useAuth();

const servicesCount = ref(0);
const lastHandledStatus = ref<string | null>(null);
const cachedUrls = ref<Record<string, string>>({});

const props = defineProps<{
  serviceId?: string;
}>();

const hasSettings = computed(() => {
  const services = settingsStore.services;
  return services && Object.keys(services).length > 0;
});

watch(
    () => settingsStore.services,
    async (services) => {
      const newServiceId = props.serviceId;
      const historyState = window.history.state;
      const currentStatus = historyState?.status;

      if (currentStatus && currentStatus !== lastHandledStatus.value) {
        lastHandledStatus.value = currentStatus;
        await toast({ message: t(`auth.errors.${currentStatus}`), preset: 'error' });

        const newState = { ...historyState, status: null };
        window.history.replaceState(newState, '');
        router.replace({ state: newState });
      }

      if (newServiceId && services?.[newServiceId]) {
        const service = services[newServiceId];

        if (!cachedUrls.value[newServiceId]) {
          cachedUrls.value[newServiceId] = await buildServiceUrl(service.url, newServiceId);
        }
      }
    },
    { immediate: true }
);
</script>

<style scoped>
.app-content-wrapper {
  height: calc(100% - var(--navbar-height) - var(--ion-safe-area-top, 0px));
  margin-block-start: calc(var(--navbar-height) + var(--ion-safe-area-top, 0px));
  width: 100%;
  display: flex;
  flex-direction: column;
}

.center-spinner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80vh;
}

.iframe-container {
  flex: 1;
  overflow: hidden;
  height: 100%;
}

.service-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

ion-content {
  --background: #fff;
}

/* Force layout engine to perfectly hide the inactive frames */
.iframe-container[style*="display: none"] {
  display: none !important;
}
</style>